import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AuditLog } from '@/types/audit';
import { UserTypeFilter } from './usePaginatedMediaData';

interface AuditLogWithProfile extends AuditLog {
  username?: string;
  avatar_url?: string;
}

interface UseAuditLogsParams {
  eventType?: string;
  userId?: string;
  startDate?: Date;
  endDate?: Date;
  page?: number;
  pageSize?: number;
  userTypeFilter?: UserTypeFilter;
}

export const useAuditLogs = (params: UseAuditLogsParams = {}) => {
  const { eventType, userId, startDate, endDate, page = 1, pageSize = 50, userTypeFilter = 'all' } = params;

  return useQuery({
    queryKey: ['audit-logs', eventType, userId, startDate, endDate, page, userTypeFilter],
    queryFn: async () => {
      let query = supabase
        .from('audit_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (eventType && eventType !== 'all') {
        query = query.eq('event_type', eventType);
      }

      if (userId) {
        query = query.eq('user_id', userId);
      }

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }

      if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data: auditData, error, count } = await query;

      if (error) throw error;

      // Fetch profiles separately for the user_ids we have
      const userIds = [...new Set((auditData || []).map(log => log.user_id))];
      
      let profilesMap: Record<string, { username: string; main_photo_url: string; onboarding_completed: boolean }> = {};
      
      if (userIds.length > 0) {
        let profilesQuery = supabase
          .from('profiles')
          .select('id, username, main_photo_url, onboarding_completed')
          .in('id', userIds);

        // Apply userTypeFilter - excluir teste de todos exceto 'test'
        switch (userTypeFilter) {
          case 'test':
            profilesQuery = profilesQuery.ilike('username', 'test-%');
            break;
          case 'sponsors':
            profilesQuery = profilesQuery.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
            break;
          case 'sponsored':
            profilesQuery = profilesQuery.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
            break;
          case 'free':
            profilesQuery = profilesQuery.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
            break;
          case 'standard':
            profilesQuery = profilesQuery.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
            break;
          case 'premium':
            profilesQuery = profilesQuery.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
            break;
          case 'active':
            profilesQuery = profilesQuery.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
            break;
          case 'pending':
            profilesQuery = profilesQuery.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
            break;
          case 'hidden':
            profilesQuery = profilesQuery.eq('hidden', true).not('username', 'ilike', 'test-%');
            break;
          case 'admins':
            // Buscar IDs de user_roles
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('role', ['admin', 'moderator']);
            const adminIds = roleData?.map(r => r.user_id) || [];
            if (adminIds.length > 0) {
              profilesQuery = profilesQuery.in('id', adminIds).not('username', 'ilike', 'test-%');
            } else {
              // Se não há admins, retornar vazio
              profilesQuery = profilesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            }
            break;
          case 'all':
          default:
            // Excluir contas de teste do "Todos"
            profilesQuery = profilesQuery.not('username', 'ilike', 'test-%');
            break;
        }
        
        const { data: profilesData } = await profilesQuery;
        
        if (profilesData) {
          profilesMap = profilesData.reduce((acc, profile) => {
            acc[profile.id] = {
              username: profile.username,
              main_photo_url: profile.main_photo_url,
              onboarding_completed: profile.onboarding_completed,
            };
            return acc;
          }, {} as Record<string, { username: string; main_photo_url: string; onboarding_completed: boolean }>);
        }
      }

      // Filter audit logs to only include users that match the profile filter
      const filteredAuditData = (auditData || []).filter(log => profilesMap[log.user_id]);

      const logs: AuditLogWithProfile[] = filteredAuditData.map((log: any) => ({
        ...log,
        username: profilesMap[log.user_id]?.username,
        avatar_url: profilesMap[log.user_id]?.main_photo_url,
      }));

      return { logs, count: filteredAuditData.length };
    },
  });
};

export const useAuditStats = (userTypeFilter: UserTypeFilter = 'all') => {
  return useQuery({
    queryKey: ['audit-stats', userTypeFilter],
    queryFn: async () => {
      const now = new Date();
      const todayStart = new Date(now.setHours(0, 0, 0, 0)).toISOString();

      // First, get all audit logs data
      const [totalResult, todayResult, typesResult, usersResult, ipsResult] = await Promise.all([
        supabase.from('audit_logs').select('user_id', { count: 'exact' }),
        supabase.from('audit_logs').select('user_id', { count: 'exact' }).gte('created_at', todayStart),
        supabase.from('audit_logs').select('event_type'),
        supabase.from('audit_logs').select('user_id').gte('created_at', todayStart),
        supabase.from('audit_logs').select('client_ip'),
      ]);

      // Get unique user IDs from all queries
      const allUserIds = new Set([
        ...(totalResult.data || []).map(r => r.user_id),
        ...(todayResult.data || []).map(r => r.user_id),
        ...(usersResult.data || []).map(r => r.user_id),
      ]);

      // Apply userTypeFilter to get filtered user IDs
      let filteredUserIds = new Set<string>();
      
      if (allUserIds.size > 0) {
        let profilesQuery = supabase
          .from('profiles')
          .select('id, username, role, plan_type, hidden, onboarding_completed')
          .in('id', Array.from(allUserIds));

        // Apply userTypeFilter
        switch (userTypeFilter) {
          case 'test':
            profilesQuery = profilesQuery.ilike('username', 'test-%');
            break;
          case 'sponsors':
            profilesQuery = profilesQuery.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
            break;
          case 'sponsored':
            profilesQuery = profilesQuery.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
            break;
          case 'free':
            profilesQuery = profilesQuery.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
            break;
          case 'standard':
            profilesQuery = profilesQuery.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
            break;
          case 'premium':
            profilesQuery = profilesQuery.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
            break;
          case 'active':
            profilesQuery = profilesQuery.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
            break;
          case 'pending':
            profilesQuery = profilesQuery.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
            break;
          case 'hidden':
            profilesQuery = profilesQuery.eq('hidden', true).not('username', 'ilike', 'test-%');
            break;
          case 'admins':
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('role', ['admin', 'moderator']);
            const adminIds = roleData?.map(r => r.user_id) || [];
            if (adminIds.length > 0) {
              profilesQuery = profilesQuery.in('id', adminIds).not('username', 'ilike', 'test-%');
            } else {
              profilesQuery = profilesQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            }
            break;
          case 'all':
          default:
            profilesQuery = profilesQuery.not('username', 'ilike', 'test-%');
            break;
        }

        const { data: profilesData } = await profilesQuery;
        filteredUserIds = new Set((profilesData || []).map(p => p.id));
      }

      // Filter results based on filtered user IDs
      const filteredTotal = (totalResult.data || []).filter(r => filteredUserIds.has(r.user_id)).length;
      const filteredToday = (todayResult.data || []).filter(r => filteredUserIds.has(r.user_id)).length;
      const filteredActiveUsers = new Set(
        (usersResult.data || []).filter(r => filteredUserIds.has(r.user_id)).map(r => r.user_id)
      ).size;

      const uniqueTypes = new Set((typesResult.data || []).map(r => r.event_type)).size;
      const uniqueIps = new Set((ipsResult.data || []).map(r => r.client_ip).filter(Boolean)).size;

      return {
        total: filteredTotal,
        today: filteredToday,
        uniqueTypes,
        activeUsers: filteredActiveUsers,
        uniqueIps,
      };
    },
  });
};
